import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Briefcase, Building2, Code2, Palette, Sparkles } from "lucide-react";
import client, { formatApiError } from "@/lib/api";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import ParticleField from "@/components/ParticleField";

const TYPES = ["Investment", "Partnership", "Technology", "Press", "Developer Opportunities", "Careers", "General Inquiry"];

const PATHWAYS = [
  { icon: Building2, t: "Investors", d: "Explore opportunities across the ImJove ecosystem." },
  { icon: Briefcase, t: "Businesses", d: "Explore partnerships and technology integrations." },
  { icon: Code2, t: "Developers", d: "Help build future ImJove technology." },
  { icon: Palette, t: "Creators", d: "Collaborate across technology, entertainment, and culture." },
  { icon: Sparkles, t: "Brands", d: "Build innovative experiences with ImJove." },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", inquiry_type: "General Inquiry", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSending(true);
    try {
      await client.post("/inquiries", form);
      toast.success("Message sent. We'll be in touch soon.");
      setForm({ name: "", email: "", company: "", inquiry_type: "General Inquiry", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to send. Try again.");
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full rounded-lg border border-white/10 bg-ink-900 px-4 py-3 font-body text-white placeholder:text-white/30 outline-none transition-colors focus:border-gold";

  return (
    <div>
      <SEO title="Contact — Build the Future With Us | ImJove Tech" description="Investors, businesses, developers, creators, and brands — start a conversation with ImJove Tech." />
      <section className="relative overflow-hidden pb-16 pt-40">
        <ParticleField className="absolute inset-0 z-0" density={40} />
        <div className="radial-fade absolute inset-0" />
        <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <p className="font-mono2 text-xs uppercase tracking-brand text-gold-light">Partnership</p>
            <h1 className="mt-4 max-w-4xl font-display text-5xl font-bold tracking-tighter text-white sm:text-7xl">
              Build the future <span className="text-gold-gradient">with us.</span>
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="pb-16">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-5">
            {PATHWAYS.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.06}>
                <div className="card-hover h-full rounded-2xl border border-white/10 bg-ink-800 p-6">
                  <p.icon size={22} className="text-gold-light" />
                  <h3 className="mt-4 font-display text-lg font-semibold text-white">{p.t}</h3>
                  <p className="mt-2 font-body text-sm text-white/50">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-28">
        <div className="mx-auto max-w-[820px] px-6 md:px-12">
          <Reveal>
            <div className="rounded-2xl border border-white/10 bg-ink-800 p-8 md:p-10">
              <h2 className="font-display text-2xl font-semibold text-white">Start a conversation</h2>
              <form onSubmit={submit} className="mt-8 space-y-5" data-testid="contact-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <input data-testid="contact-name" className={inputCls} placeholder="Full name" value={form.name} onChange={set("name")} />
                  <input data-testid="contact-email" type="email" className={inputCls} placeholder="Email address" value={form.email} onChange={set("email")} />
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <input data-testid="contact-company" className={inputCls} placeholder="Company (optional)" value={form.company} onChange={set("company")} />
                  <select data-testid="contact-type" className={inputCls} value={form.inquiry_type} onChange={set("inquiry_type")}>
                    {TYPES.map((t) => <option key={t} value={t} className="bg-ink-900">{t}</option>)}
                  </select>
                </div>
                <textarea data-testid="contact-message" className={`${inputCls} min-h-[140px] resize-y`} placeholder="Tell us about your idea, opportunity, or question…" value={form.message} onChange={set("message")} />
                <button type="submit" disabled={sending} data-testid="contact-submit" className="btn-gold inline-flex items-center gap-2 rounded-full px-8 py-3.5 font-mono2 text-[12px] font-semibold uppercase tracking-widest disabled:opacity-60">
                  {sending ? "Sending…" : "Start a Conversation"} <ArrowRight size={15} />
                </button>
              </form>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
